import type { ReactNode } from 'react';
import { cn } from '../cn.ts';

/**
 * Ba bề rộng, đúng ba giá trị của thiết kế:
 * `content` 1240px (bề rộng nội dung của artboard) · `narrow` 900px (`#faq`, `#cta`) · `prose`.
 *
 * Padding ngang dùng `--spacing-gutter` (20 → 32px, đã clamp) chứ không phải bậc breakpoint:
 * gutter là một giá trị liên tục trong thiết kế, cắt nó thành 3 bậc là tạo ba nguồn sự thật.
 */
export interface ContainerProps {
  readonly children: ReactNode;
  readonly width?: 'content' | 'narrow' | 'prose';
  /**
   * Gutter nằm NGOÀI hay TRONG bề rộng tối đa — thiết kế dùng cả hai và chúng lệch nhau 64px.
   *
   * `outside` (mặc định): `<section>` mang padding ngang, bọc một div `max-width:1240px` ⇒ nội
   * dung rộng đủ 1240. Đây là kiểu của 11/13 section.
   * `inside`: hero (`template.html:608`) đặt `max-width:1240px` VÀ `padding:0 32px` trên CÙNG một
   * div ⇒ nội dung chỉ còn 1176.
   *
   * Bản đầu của `Container` chỉ có kiểu `inside` và áp cho tất cả, nên mọi section không phải hero
   * hẹp hơn thiết kế 64px. Mắt không thấy; cổng `design-diff` thấy ngay — mọi phần tử của `#pm`
   * lệch đúng 32px.
   */
  readonly gutter?: 'outside' | 'inside';
  readonly className?: string;
}

/** Gutter nằm trong bề rộng tối đa — khai bao nhiêu thì hộp rộng bấy nhiêu. */
const WIDTH_INSIDE = {
  content: 'max-w-content',
  narrow: 'max-w-narrow',
  // Bề rộng prose KHÔNG đi qua token nếu viết dạng ngắn: Tailwind v4 có một utility tĩnh trùng
  // đúng tên đó và nó thắng phép tra namespace của `@theme`, phân giải thành 65ch. Đo trên CSS
  // build thật, trong khi `--container-prose: 44rem` bị loại khỏi output vì không ai tham chiếu.
  // Viết thẳng `var()` là cách duy nhất buộc nó về token — hai bề rộng kia không trùng tên nên
  // giữ dạng ngắn. (Cú pháp class cố tình KHÔNG viết ra ở đây: Tailwind quét comment như văn bản
  // thuần, nhắc tên là nó hồi sinh đúng cái utility ta vừa bỏ.)
  prose: 'max-w-[var(--container-prose)]',
} as const;

/** Gutter nằm ngoài — cộng hai lần gutter để phần NỘI DUNG đúng bề rộng đã khai. */
const WIDTH_OUTSIDE = {
  content: 'max-w-[calc(var(--container-content)+2*var(--spacing-gutter))]',
  narrow: 'max-w-[calc(var(--container-narrow)+2*var(--spacing-gutter))]',
  prose: 'max-w-[calc(var(--container-prose)+2*var(--spacing-gutter))]',
} as const;

export function Container({
  children,
  width = 'content',
  gutter = 'outside',
  className,
}: ContainerProps) {
  const max = gutter === 'inside' ? WIDTH_INSIDE[width] : WIDTH_OUTSIDE[width];
  return <div className={cn('mx-auto w-full px-gutter', max, className)}>{children}</div>;
}
